import { useContext, useLayoutEffect, useMemo, useState } from "react";
import useAuth from "../../../../../../Hooks/useAuth";
import CustomizerContext from "../../../../../../_helper/Customizer";
import useAxiosPrivate from "../../../../../../Hooks/useAxiosPrivate";
import { FixedAssetClassTypes } from "../types";
import { toast } from "react-toastify";
import {StatusRow} from "../../../../../Finance/Settings/ReportType/List/ReportTypeList";
import { formatDateWithTimeZone } from "../../../../../../_helper/dateHelper";
import { FixedAssetClassApi, FixedAssetGroupApi } from "../../../../../../api";
import {
  Name,
  Status,
  Description,
  DateCreated,
  DateModified,
  FixedAssetClass,
  Code,
  FixedAssetGroup,
  UsefulLife,
  InsuranceRate,
  AnnualAllowanceRate,
  InitialAllowanceRate,
  InvestmentAllowanceRate,
} from "../../../../../../Constant";
import { createDropdownField } from "../../../../../../Data/FormField/PublicData";


export default function useFixedAssetClass() {
  const { userOrganisationId }: any = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const title = FixedAssetClass;
  const [data, setData] = useState<FixedAssetClassTypes[]>([]);
  const [formFields, setFormFields] = useState<any[]>([
    {
      id: "name",
      name: "name",
      label: Name,
      type: "text",
      value: "",
      required: true,
      colSize: 6,
    },
    {
      id: "investment_allowance_rate",
      name: "investment_allowance_rate",
      label: InvestmentAllowanceRate,
      type: "number",
      value: "",
      required: true,
      colSize: 6,
    },
    {
      id: "initial_allowance_rate",
      name: "initial_allowance_rate",
      label: InitialAllowanceRate,
      type: "number",
      value: "",
      required: true,
      colSize: 6,
    },
    {
      id: "annual_allowance_rate",
      name: "annual_allowance_rate",
      label: AnnualAllowanceRate,
      type: "number",
      value: "",
      required: true,
      colSize: 6,
    },
    {
      id: "insurance_rate",
      name: "insurance_rate",
      label: InsuranceRate,
      type: "number",
      value: "",
      required: true,
      colSize: 6,
    },
    {
      id: "useful_life",
      name: "useful_life",
      label: UsefulLife,
      type: "number",
      value: "",
      required: true,
      colSize: 6,
    },
    {
      id: "description",
      name: "description",
      label: Description,
      type: "textarea",
      value: "",
      required: true,
      colSize: 6,
    },
    {
      id: "is_active",
      name: "is_active",
      label: Status,
      type: "switch",
      value: "1",
      colSize: 6,
    },
  ]);

  const tableColumns = [
    {
      name: Name,
      selector: (row: (typeof data)[0]) => `${row.name}`,
      sortable: true,
      center: false,
    },
    {
      name: Code,
      selector: (row: (typeof data)[0]) => `${row.code}`,
      sortable: true,
      center: false,
    },
    {
      name: UsefulLife,
      selector: (row: (typeof data)[0]) => `${row.useful_life}`,
      sortable: true,
      center: false,
    },
    {
      name: FixedAssetGroup,
      selector: (row: (typeof data)[0]) => `${row.administration_fixed_asset_group.name}`,
      sortable: true,
      center: false,
    },

    {
      name: Status,
      selector: (row: (typeof data)[0]) => `${row.is_active}`,
      sortable: true,
      center: true,
      cell: (row: (typeof data)[0]) => StatusRow(row),
    },
    {
      name: DateCreated,
      selector: (row: (typeof data)[0]) =>
        `${formatDateWithTimeZone(row.created_at)}`,
      sortable: false,
      center: false,
    },

    {
      name: DateModified,
      selector: (row: (typeof data)[0]) =>
        `${formatDateWithTimeZone(row.updated_at)}`,
      sortable: false,
      center: false,
    },
  ];

  const FixedAssetGroupDropDown = createDropdownField(
      axiosPrivate,
      "administration_fixed_asset_group_id",
      "administration_fixed_asset_group_id",
      FixedAssetGroup,
      `${FixedAssetGroupApi}?organisation_id=${userOrganisationId}`,
      "",
      6
  );

  async function updateFields() {
      const updatedFields = [FixedAssetGroupDropDown, ...formFields];
      await FixedAssetGroupDropDown.fetchOptions();
      setFormFields(updatedFields);
  }

  useLayoutEffect(() => {
    updateFields();
  }, []);

  const { layoutURL } = useContext(CustomizerContext);
  const newBtn = `${process.env.PUBLIC_URL}/administration/settings/fixed-asset/groups/create/${layoutURL}`;
  const [loading, setLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<
    (typeof data)[0] | null
  >(null);
  const [editMode, setEditMode] = useState(false);

  const fetchData = () => {
    setLoading(true);
    axiosPrivate
      .get(
        `${FixedAssetClassApi}?with_trashed=${false}&organisation_id=${userOrganisationId}`
      )
      .then((res) => {
        const response: FixedAssetClassTypes[] = res.data.data;
        setData(response);

        setLoading(false);
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          toast.error(err.response.data.detail);
        } else {
          toast.error("Whoops! Something went wrong");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  useLayoutEffect(() => {
    fetchData();
  }, []);

  //
  const handleEditRec = async (
    id: number | string,
    editRecord: (typeof data)[0] | any
  ) => {
    await axiosPrivate
      .put(`${FixedAssetClassApi}/${id}`, {
        ...editRecord,
        organisation_id: userOrganisationId,
      })
      .then((_res) => {
        const updatedData = data.map((row) =>
          row.id === selectedRowData!.id ? { ...row, ..._res.data.data } : row
        );
        setData(updatedData);
        toast.success(`${_res.data.data.name} Editted Successfully.`);
      })
      .catch((err) => {
        if (err.response && err.response.status === 422) {
          toast.error("Validation Errors");
        } else if (err.response && err.response.status === 400) {
          toast.error(err.response.data.detail);
        } else {
          toast.error("Whoops! Something went wrong");
        }
        // throw err;
        toast.error(err.response.message || "Whoops! Something went wrong");
      });
  };

  const handleEdit = (row: (typeof data)[0] | any) => {
    setViewModalOpen(true);
    setEditMode(true);
    row.administration_fixed_asset_group_id = row.administration_fixed_asset_group.id
    setSelectedRowData(row);
  };

  const memoizedHandleEdit = useMemo(
    () => handleEditRec,
    [data, selectedRowData]
  );

  const handleView = (row: (typeof data)[0]) => {
    setViewModalOpen(true);
    setEditMode(false);
    setSelectedRowData(row);
  };

  return {
    handleView,
    data,
    tableColumns,
    viewModalOpen,
    selectedRowData,
    editMode,
    newBtn,
    title,
    formFields,
    loading,
    setViewModalOpen,
    handleEdit,
    memoizedHandleEdit,
  };
}
